import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Select,
  TextField,
  Button,
  Spinner,
  Alert,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  type ManagedVendorItem,
  type ManagedVendorFilter,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { StoreChip, VendorStatusChip } from '../components/VendorChips';

/**
 * Manage vendors — list view (Figma "Manage vendors (list)"). Approved vendors
 * with their store-type + lifecycle-status chips and current commission;
 * "Manage" opens the detail. Companion to the Vendor-applications console.
 */

const GRID = '1.8fr 1.3fr 0.8fr 1fr 1fr 0.7fr';

const STATUS_OPTIONS: { value: ManagedVendorFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'blocked', label: 'Blocked' },
];

function locationOf(v: ManagedVendorItem): string {
  const a = v.address;
  if (!a?.city && !a?.state) return '—';
  return [a?.city, a?.state].filter(Boolean).join(', ');
}
function commissionPct(v: ManagedVendorItem): string {
  const bps = v.commissionSchedules[0]?.rateBps;
  return bps == null ? '—' : `${(bps / 100).toFixed(1)}%`;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function ManageVendors() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canManage = caps.has('vendor:manage');
  const navigate = useNavigate();

  const [status, setStatus] = useState<ManagedVendorFilter>('all');
  const [sort, setSort] = useState<'name' | 'newest'>('name');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<ManagedVendorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listVendors({ status });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const visible = useMemo(() => {
    let list = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((v) => v.businessName.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) =>
      sort === 'name'
        ? a.businessName.localeCompare(b.businessName)
        : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [items, search, sort]);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view vendors.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Manage vendors
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Active marketplace vendors — edit profiles, adjust commission, and suspend or reactivate.
      </Typography>

      {!canManage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          label="Search vendors by name"
          ariaLabel="Search vendors"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Sort"
          label="Sort"
          native
          size="small"
          sx={{ width: 180 }}
          value={sort}
          onChange={(_e, v) => setSort(v as 'name' | 'newest')}
          options={[
            { value: 'name', label: 'Name (A–Z)' },
            { value: 'newest', label: 'Recently added' },
          ]}
        />
        <Select
          ariaLabel="Filter by status"
          label="Status"
          native
          size="small"
          sx={{ width: 180 }}
          value={status}
          onChange={(_e, v) => setStatus(v as ManagedVendorFilter)}
          options={STATUS_OPTIONS}
        />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : visible.length === 0 ? (
        <EmptyState icon="shop" title="No vendors" body="No vendors match this filter." />
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 2,
              px: 2,
              py: 1.25,
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            {['Business', 'Location', 'Commission', 'Type', 'Status', ''].map((h, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}
              >
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>
          {visible.map((v, idx) => (
            <Box
              key={v.vendorId}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 2,
                px: 2,
                py: 1.5,
                alignItems: 'center',
                borderBottom: idx < visible.length - 1 ? '1px solid #eef2f6' : 'none',
                '&:hover': { backgroundColor: '#fafbfc' },
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{v.businessName}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {locationOf(v)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {commissionPct(v)}
              </Typography>
              <Box>
                <StoreChip businessType={v.businessType} />
              </Box>
              <Box>
                <VendorStatusChip status={v.status} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  ariaLabel={`Manage ${v.businessName}`}
                  buttonText="Manage"
                  variant="outlined"
                  size="small"
                  sx={NEUTRAL_BTN}
                  onClick={() => navigate(`/manage-vendors/${v.vendorId}`)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
